import { formatDateTime } from '@if/core/utils'
import DeleteIcon from '@mui/icons-material/Delete'
import DescriptionIcon from '@mui/icons-material/Description'
import EmailIcon from '@mui/icons-material/Email'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { CopyButton } from '@src/components/CopyButton'
import { CreditAppLink } from '@src/components/CreditAppLink'
import { StatusChip } from '@src/components/StatusChip'
import { DocsAutomation, EProcessingStatus } from '@src/types/automationDocument'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import TagsCell from './TagsCell'

/** Ensure ISO strings without timezone suffix are treated as UTC */
const toUtc = (date: string | null | undefined) =>
  date && !date.endsWith('Z') ? date + 'Z' : date

interface ActionsProps {
  onView: (item: DocsAutomation) => void
  onDelete: (item: DocsAutomation) => void
  onMessage: (item: DocsAutomation) => void
}

export const useDataGridColumns = ({
  onView,
  onDelete,
  onMessage,
}: ActionsProps): GridColDef<DocsAutomation>[] => {
  const { t } = useTranslation()
  return useMemo<GridColDef<DocsAutomation>[]>(
    () => [
      {
        field: 'id',
        headerName: t('docsAutomation.ref'),
        width: 150,
        sortable: false,
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row }) => {
          const subject = row.email?.subject
          if (subject) {
            return (
              <Tooltip title={subject}>
                <Typography variant="body2" noWrap>
                  {subject}
                </Typography>
              </Tooltip>
            )
          }
          return (
            <Typography variant="caption" fontFamily="monospace" noWrap>
              #{row.id.slice(-6)}
            </Typography>
          )
        },
      },
      {
        field: 'processingStatus',
        headerName: t('docsAutomation.processingStatus'),
        width: 120,
        align: 'left',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          if (!row.processingStatus) return null
          return <Box data-tour="status-cell"><StatusChip status={row.processingStatus} /></Box>
        },
      },
      {
        field: 'sentBy',
        headerName: t('docsAutomation.from'),
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row }) => {
          if (row.email) {
            return (
              <Stack direction="row" alignItems="center" gap={0.5} overflow="hidden">
                <Typography variant="body2" noWrap>
                  {row.email.sentBy || row.email.senderEmail}
                  {row.email.sentBy && (
                    <Typography component="span" variant="body2">
                      {' '}
                      ({row.email.senderEmail})
                    </Typography>
                  )}
                </Typography>
                <CopyButton value={row.email.senderEmail} label={t('common.copy')} />
              </Stack>
            )
          }
          return (
            <Tooltip title={`${row.originSystem.toUpperCase()} System`}>
              <Typography variant="body2" noWrap>
                {row.createdByUserFullname || `${row.originSystem.toUpperCase()} System`}
              </Typography>
            </Tooltip>
          )
        },
      },
      {
        field: 'tags',
        headerName: t('docsAutomation.tags'),
        flex: 1.5,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => <TagsCell document={row} />,
      },
      {
        field: 'linkedCreditApp',
        headerName: t('docsAutomation.linkedApplication'),
        width: 250,
        sortable: false,
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row }) => (
          <CreditAppLink
            creditApp={row.linkedCreditApp}
            possibleRefs={row.extractedReferenceNumbers}
          />
        ),
      },
      {
        field: 'receivedOn',
        headerName: t('common.emailReceived'),
        minWidth: 170,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_value: unknown, row: DocsAutomation) => toUtc(row.email?.receivedOn),
        valueFormatter: (value: string) => {
          if (!value || value === '0001-01-01T00:00:00Z') return ''
          return formatDateTime(value)
        },
      },
      {
        field: 'createdOn',
        headerName: t('common.created'),
        minWidth: 170,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_value: unknown, row: DocsAutomation) => toUtc(row.createdOn),
        valueFormatter: (value: string) => {
          if (!value || value === '0001-01-01T00:00:00Z') return ''
          return formatDateTime(value)
        },
      },
      {
        field: 'updatedOn',
        headerName: t('common.updated'),
        minWidth: 170,
        sortable: true,
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row }) => {
          if (!row.updatedOn || row.updatedOn === '0001-01-01T00:00:00') return null
          return (
            <Typography variant="body2" noWrap>
              {formatDateTime(toUtc(row.updatedOn))}
            </Typography>
          )
        },
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        resizable: false,
        align: 'right',
        width: 150,
        renderCell: ({ row }) => {
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Tooltip title={t('common.viewDocument')}>
                <IconButton size="small" href={`/documents/${row.id}`}>
                  <DescriptionIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.details')}>
                <IconButton size="small" onClick={() => onView(row)} data-tour="action-btn-link">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {row.email?.message && (
                <Tooltip title={t('docsAutomation.message')}>
                  <IconButton size="small" onClick={() => onMessage(row)} data-tour="action-btn-message">
                    <EmailIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {![EProcessingStatus.Discarded, EProcessingStatus.Finalized].includes(row.processingStatus) && (
                <Tooltip title={t('common.delete')}>
                  <IconButton size="small" onClick={() => onDelete(row)} color="error" data-tour="action-btn-delete">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )
        },
      },
    ],
    [onDelete, onMessage, onView, t],
  )
}
